import { GlobalStore } from '@roc/client-portal-shared/stores';
import { action, flow, makeObservable, observable, override } from 'mobx';
import { FormStore, UserStore } from '@roc/feature-app-core';
import { GridStore } from '@roc/feature-app-core';
import { SelectBorrowersStore } from '@roc/feature-borrowers';
import { ApiResponse } from '@roc/feature-app-core';
import { Borrower } from '@roc/feature-types';
import { LoanParticipantsStore } from '@roc/feature-loan-participants';
import {
  GeneralTaskAttachment,
  GeneralTaskComment,
  GeneralTasksView,
  Task,
  TaskDocumentDownloadResponse,
  TaskStatus,
} from '../types/generalTasksTypes';
import { GeneralTasksGridStore } from './generalTasksGridStore';
import { GeneralTasksStore } from './generalTasksStore';
import { DialogState, FileUpload } from '@roc/ui';
import {
  formatDate,
  DateFormat,
  GENERIC_ERROR_MESSAGE,
  downloadDocument,
} from '@roc/feature-utils';
import { GeneralTasksService } from '../services/generalTasksService';
import { assign } from 'mobx/dist/internal';
import { EditTaskStore } from './editTaskStore';

export class GeneralTasksAttachmentStore {
  private globalStore: GlobalStore;
  private editTasksStore: EditTaskStore;
  private generalTaskService: GeneralTasksService;

  showAttachmentsDialog: boolean;
  documentPreview;

  constructor(globalStore: GlobalStore, editTasksStore: EditTaskStore) {
    this.globalStore = globalStore;
    this.editTasksStore = editTasksStore;
    this.generalTaskService = new GeneralTasksService();

    makeObservable(this, {
      showAttachmentsDialog: observable,
      documentPreview: observable,
      downloadDocument: observable,
      closeDocumentPreview: action,
      closeAttachmentDialog: action,
      openAttachmentDialog: flow,
      previewDocument: flow,
      addAttachments: flow,
      removeAttachment: flow
    });
  }

  reset() {
    this.showAttachmentsDialog = false;
    this.documentPreview = null;
  }

  *openAttachmentDialog(){
    // Task must be saved before adding an attachment
    if (!this.editTasksStore.taskV2.taskId) {
      const success = yield this.editTasksStore.saveTask();
      if(!success){
        return;
      }
    }

    this.showAttachmentsDialog = true;
  }

  closeAttachmentDialog() {
    this.showAttachmentsDialog = false;
  }

  private getDownloadPayload(response): {
    bytes: Uint8Array;
    headers: Record<string, string>;
    fileName: string;
  } {
    const payload: TaskDocumentDownloadResponse = response?.data?.data;
    const fileName = payload?.originalFileName ?? 'download';
    const contentType = payload?.contentType ?? 'application/octet-stream';
    const headers = {
      'content-type': contentType,
      'content-disposition': `attachment; filename="${fileName}"`,
    };

    if (Array.isArray(payload?.docBytesArray)) {
      return {
        bytes: new Uint8Array(payload.docBytesArray),
        headers,
        fileName,
      };
    }

    const base64 = payload?.docBytesArray ?? '';
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));

    return {
      bytes,
      headers,
      fileName,
    };
  }

  *previewDocument(attachment: GeneralTaskAttachment){
    const response = yield this.generalTaskService.downloadDocument(attachment.documentId);
    const { bytes, headers, fileName } = this.getDownloadPayload(response);
    this.documentPreview = {
      title: fileName,
      data: bytes,
      headers,
    };
  }

  *downloadDocument(attachment: GeneralTaskAttachment){
    const response = yield this.generalTaskService.downloadDocument(
      attachment.documentId
    );
    const { bytes, headers, fileName } = this.getDownloadPayload(response);
    downloadDocument(
      bytes,
      headers,
      'download',
      fileName
    );
  }

  *addAttachments(uploads: FileUpload[]) {
    try {
      const taskId = this.editTasksStore.taskV2.taskId;
      if (!taskId || !Array.isArray(uploads) || uploads.length === 0) {
        throw new Error('Missing task id or uploads');
      }

      const responses = yield Promise.all(
        uploads.map(upload => {
          const attachment = {
            taskId,
            attachmentName: upload.name,
          };
          return this.generalTaskService.uploadAttachment(
            attachment,
            upload.file
          );
        })
      );

      const updatedTask = responses
        .map(response => response?.data?.data)
        .find(task => task && typeof task === 'object' && task.taskId === taskId);

      if (updatedTask) {
        this.editTasksStore.loadTask(updatedTask);
      } else {
        yield this.editTasksStore.reloadTaskById(taskId);
      }

      this.closeAttachmentDialog();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Attachment uploaded successfully!'
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *removeAttachment(attachment: GeneralTaskAttachment){
    try {
      yield this.generalTaskService.removeAttachment(attachment.taskAttachmentId);
      const attachments = this.editTasksStore.attachments.filter(item => item !=attachment)
      this.editTasksStore.setAttachments(attachments);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Attachment removed successfully!'
      })
    } catch(e){
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  closeDocumentPreview(){
    this.documentPreview = null;
  }
}
